import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mic, PhoneOff, Phone, AlertTriangle, Gem } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { GEMS_PER_MINUTE, MAX_CALL_DURATION_MINUTES, useCall } from '@/contexts/CallContext';
import { cn } from '@/lib/utils';
import { PipecatClientAudio } from '@pipecat-ai/client-react';

function EhCallManager() {
  const {
    callState,
    startCall,
    endCall,
    endReason,
    character,
    errorMessage,
    elapsedTime,
    showLowBalanceWarning,
    remainingCallTime,
    resetCall,
    forceOpenManager
  } = useCall();
  
  const [micPermission, setMicPermission] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  
  const [isOpen, setIsOpen] = useState(false);

  // ringtone
  const ringtoneRef = useRef<HTMLAudioElement>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);
  useEffect(() => {
    const audio = ringtoneRef.current;
    if (!audio) return;

    if (callState === 'requesting_url' || callState === 'connecting') {
      const isPlaying = !audio.paused || audio.currentTime > 0;
      if (!isPlaying) {
        const promise = audio.play();
        if (promise) {
          playPromiseRef.current = promise;
          promise.catch(() => {
            // Autoplay was prevented
            if (import.meta.env.DEV) console.log('ringtone autoplay prevented');
            playPromiseRef.current = null;
          });
        }
      }
    } else {
      const safePause = () => {
        if (ringtoneRef.current && !ringtoneRef.current.paused) {
          ringtoneRef.current.pause();
          ringtoneRef.current.load(); // reset the audio
          ringtoneRef.current.currentTime = 0;
        }
        playPromiseRef.current = null;
      };

      if (playPromiseRef.current) {
        playPromiseRef.current.then(safePause).catch(safePause);
      } else {
        safePause();
      }
    }
  }, [callState]);

  useEffect(() => {
    if (callState !== 'idle' || forceOpenManager) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [callState, forceOpenManager]);

  useEffect(() => {
    const checkMicPermission = async () => {
      if (isOpen) {
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          setMicPermission('granted');
        } catch (_) {
          // do nothing, button lets the user try again manually
        }
      }
    }
    checkMicPermission();
  }, [isOpen]);


  const requestMicPermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
    } catch (error) {
      console.error('Microphone permission denied', error);
      setMicPermission('denied');
    }
  }, []);
  
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    if (!newOpen) {
      // keep the call open when navigating. User has to click the phone icon to end the call.
      // if (callState === 'connected' || callState === 'connecting') {
      //   endCall();
      // }
      if (['idle', 'pre-call', 'ended', 'error'].includes(callState)) {
        if (import.meta.env.DEV) console.log('CallContext.resetCall after closing EhCallManager');
        setTimeout(resetCall, 300);
      }
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = Math.round(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const renderContent = () => {
    switch (callState) {
      case 'idle':
        return (
          <SheetHeader className="text-center">
            <SheetTitle className="text-xl">Phone Call</SheetTitle>
            <SheetDescription>
              No active call
            </SheetDescription>
          </SheetHeader>
        );
      case 'pre-call':
        return (
          <>
            <SheetHeader className="text-center">
              <SheetTitle className="text-2xl">{`Call ${character?.first_name}`}</SheetTitle>
              <SheetDescription>
                <span className="text-base text-gray-500">{`Speak directly with ${character?.first_name} in more than 30 languages.`}</span>
                <br />
                <br />
                <span className="text-lg">For the best call experience:</span>
                <br />
                <span className="text-base">{`• Chat a bit with ${character?.first_name} before the call to set the mood if needed`}</span>
                <br />
                <span className="text-base">• Use a headset or earbuds for better audio quality</span>
              </SheetDescription>
            </SheetHeader>
            <div className="p-4 max-w-md flex flex-col gap-4">
              {micPermission === 'prompt' && (
                <Button onClick={requestMicPermission} className="w-full" size="lg" variant="outline">
                  <Mic className="mr-2" />
                  <span>Enable Microphone</span>
                </Button>
              )}
              {micPermission === 'denied' && (
                <p className="text-rose-500 text-center">Microphone access is required. Please enable it in your browser settings and refresh the page.</p>
              )}
              <Button
                onClick={startCall}
                disabled={micPermission !== 'granted'}
                variant="pink"
                size="lg"
                >
                <Phone className="mr-2" />
                <span className="text-lg font-bold">Start Call</span>
              </Button>
              <div className="text-center text-sm text-gray-500 -mt-2">{`Calls cost ${GEMS_PER_MINUTE} gems per minute.`}</div>
            </div>
            <SheetFooter className="p-4 max-w-md">
              <SheetClose asChild><Button variant="ghost">Cancel</Button></SheetClose>
            </SheetFooter>
          </>
        );
      case 'requesting_url':
        return (
          <SheetHeader className="flex flex-col items-center justify-center h-full p-4">
            <SheetTitle className="text-xl">Starting Call</SheetTitle>
            <div className={cn('flex size-14 items-center justify-center rounded-full border-4 border-green-500')}>
              <Phone className={cn('size-7 text-green-500 animate-pulse')} />
            </div>
            <SheetDescription className="text-lg my-2">Please wait...</SheetDescription>
          </SheetHeader>
        )
      case 'connecting':
      case 'connected':
        if (showLowBalanceWarning) {
          return (
            <>
              <SheetHeader className="text-center">
                <SheetTitle className="text-xl text-amber-400 flex items-center justify-center gap-2">
                    <AlertTriangle />
                    <span>Low Balance</span>
                </SheetTitle>
                <SheetDescription className="text-base text-foreground">
                  {`Your call will end in approximately ${formatTime(remainingCallTime)}.`}
                </SheetDescription>
              </SheetHeader>
              <SheetFooter className="pt-4 max-w-md">
                <Button
                  onClick={() => setIsOpen(false)}
                  className="bg-blue-500 hover:bg-blue-400"
                  size="lg"
                  asChild
                >
                  <Link to="/gems">
                    <Gem className="mr-2 h-4 w-4" />
                    <span>Add Gems</span>
                  </Link>
                </Button>
                <Button onClick={() => endCall('user_disconnected')} variant="ghost" size="lg">
                  <PhoneOff className="mr-2 h-4 w-4" />
                  <span>End Call Now</span>
                </Button>
              </SheetFooter>
            </>
          )
        }
        return (
          <div className="flex w-full items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <div className={cn('flex size-14 items-center justify-center rounded-full', callState === 'connected' && 'border-4 border-green-500 ')}>
                <Phone className={cn('size-7', callState === 'connected' ? 'text-green-500 animate-pulse' : 'text-gray-400')} />
              </div>
              <div className="text-left">
                <SheetTitle className="text-lg">{callState === 'connecting' ? 'Connecting...' : `${character?.first_name}`}</SheetTitle>
                <SheetDescription>
                  <span>{formatTime(elapsedTime)}</span>
                  {elapsedTime > (MAX_CALL_DURATION_MINUTES * 0.8) * 60 && (
                    <span className="text-xs block">
                      {`Calls are limited to ${MAX_CALL_DURATION_MINUTES} minutes.`}
                    </span>
                  )}
                </SheetDescription>
              </div>
            </div>
            <Button onClick={() => endCall('user_disconnected')} variant="destructive" className="h-14 w-14 rounded-full max-sm:mr-8">
              <PhoneOff className="size-7" />
              <span className="sr-only">End Call</span>
            </Button>
          </div>
        );
      case 'ended': {
        const callDurationMinutes = elapsedTime / 60;
        const gemCost = Math.round(callDurationMinutes * GEMS_PER_MINUTE * 10) / 10;
        return (
            <>
              <SheetHeader className="text-center">
                <SheetTitle className="text-xl">Call Ended {endReason === 'low_balance' ? 'due to low 💎 balance 😢' : '😊'}</SheetTitle>
                <SheetDescription className="text-base">
                  <span>{`Duration: ${formatTime(elapsedTime)} - ${gemCost} gems`}</span>
                  {errorMessage && (
                    <span className="block text-rose-500 text-sm mt-2">
                      {errorMessage}
                    </span>
                  )}
                </SheetDescription>
              </SheetHeader>
              <SheetFooter className="pt-4">
                <SheetClose asChild><Button variant="outline" className="w-full">Close</Button></SheetClose>
              </SheetFooter>
            </>
        );
      }
      case 'error':
        return (
          <>
            <SheetHeader className="text-center">
              <SheetTitle className="text-xl text-rose-500">Call Error</SheetTitle>
              <SheetDescription className="text-md">{errorMessage || 'Sorry for this error. Please try again later.'}</SheetDescription>
            </SheetHeader>
            <SheetFooter className="pt-4">
              <SheetClose asChild><Button variant="outline" className="w-full">Close</Button></SheetClose>
            </SheetFooter>
          </>
        )
    }
  }

  const isCallInProgress = callState === 'requesting_url' || callState === 'connecting' || callState === 'connected';

  const showFloatingButton = !isOpen && isCallInProgress;

  const isRinging = callState === 'requesting_url' || callState === 'connecting';

  return (
    <>
    <audio ref={ringtoneRef} src={isRinging ? '/sounds/us_ringback_tone.mp3' : undefined} preload="auto" loop={true} />
    <Sheet open={isOpen} onOpenChange={handleOpenChange} modal={!isCallInProgress}>
      <SheetContent 
        side="bottom"
        className={cn(
          'flex items-center',
          'transition-all duration-300 ease-in-out bg-zinc-900/90 border-zinc-800 text-white',
          isCallInProgress && !showLowBalanceWarning ? '' : 'h-auto',
          showLowBalanceWarning && 'h-56'
        )}
        onInteractOutside={(e) => {
          if (isCallInProgress) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isCallInProgress) e.preventDefault();
        }}
      >
        <div className="container flex flex-col items-center">
          {/* Pipecat audio input/output */}
          <PipecatClientAudio />
          {renderContent()}
        </div>
      </SheetContent>
    </Sheet>
    {showFloatingButton && (
      <Button
        variant="default"
        className={cn(
          'fixed bottom-8 right-8 z-50 size-14 rounded-full p-3',
          callState === 'connected' && 'border-4 border-green-500'
        )}
        onClick={() => setIsOpen(true)}
      >
        <Phone className={cn('size-8', callState === 'connected' ? 'text-green-500 animate-pulse' : 'text-gray-400')} />
      </Button>
    )}
    </>
  );
}

export default EhCallManager;
